import { TFunction } from "i18next";
import { format, isValid, parse } from "date-fns";

import { ListPerson } from "../../../List/schema/list";
import {
	getLastSentDate,
	getSentCategory,
	SCHEDULED_DATE_CATEGORY_FORMAT,
	SCHEDULED_WORD_CATEGORY,
} from "./outbox-category.helper";

export interface DividerContent {
	text: string;
	bgClass: string;
	textClass: string;
}

const SENT_STYLE = { bgClass: "bg-gray-200", textClass: "text-gray-700" };
const SCHEDULED_STYLE = { bgClass: "bg-green-200", textClass: "text-green-800" };

/** Header label per word category — one entry per member of `SCHEDULED_WORD_CATEGORY`. */
const SCHEDULED_WORD_LABEL_KEYS: Record<string, string> = {
	[SCHEDULED_WORD_CATEGORY.TODAY]: "outbox.dividers.scheduledToday",
	[SCHEDULED_WORD_CATEGORY.TOMORROW]: "outbox.dividers.scheduledTomorrow",
	[SCHEDULED_WORD_CATEGORY.NEXT_WEEK]: "outbox.dividers.scheduledNextWeek",
	[SCHEDULED_WORD_CATEGORY.LATER]: "outbox.dividers.scheduledLater",
	[SCHEDULED_WORD_CATEGORY.ASSIGNED]: "outbox.dividers.waitingToStart",
};

/**
 * Header for a scheduled bucket. A word category has its own label and a this-week category is a
 * date; anything else gets the plain "Scheduled" — never the category key itself (PREC-4237).
 */
const getScheduledDividerText = (category: string, t: TFunction): string => {
	const labelKey = SCHEDULED_WORD_LABEL_KEYS[category];

	if (labelKey) {
		return t(labelKey);
	}

	if (isValid(parse(category, SCHEDULED_DATE_CATEGORY_FORMAT, new Date()))) {
		return t("outbox.dividers.scheduledOn", { date: category });
	}

	return t("outbox.dividers.scheduled");
};

/** Divider bucket for leads with no outbound message yet (All tab, sorted last). */
export const NOTHING_SENT_CATEGORY = "nothing-sent";

/**
 * All-tab grouping (PREC-3850): rows are ordered by last message sent, so the
 * dividers group by send day — "nothing sent yet" leads form the trailing group.
 */
export const getAllTabCategory = (listPerson: ListPerson): string => {
	const sentDate = getLastSentDate(listPerson);

	return sentDate ? getSentCategory(sentDate) : NOTHING_SENT_CATEGORY;
};

/**
 * Maps an Outbox divider bucket (from `getCurrentCategory`) to its group-header label
 * and styling. Kept pure so the mapping is unit-testable without rendering the column.
 */
export const getDividerContent = (category: string, t: TFunction): DividerContent => {
	if (category === NOTHING_SENT_CATEGORY) {
		return {
			text: t("outbox.dividers.nothingSentYet"),
			...SENT_STYLE,
		};
	}

	if (category === "paused") {
		return {
			text: t("outbox.dividers.paused"),
			bgClass: "bg-gray-700",
			textClass: "text-gray-100",
		};
	}

	if (category.startsWith("sent-")) {
		if (category === "sent-today") {
			return { text: t("outbox.dividers.sentToday"), ...SENT_STYLE };
		}

		if (category === "sent-yesterday") {
			return { text: t("outbox.dividers.sentYesterday"), ...SENT_STYLE };
		}

		if (category === "sent-unknown") {
			return { text: t("outbox.dividers.sentUnknown"), ...SENT_STYLE };
		}

		const dateStr = category.replace("sent-", "");
		const sentDate = parse(dateStr, "yyyy-MM-dd", new Date());

		return {
			text: t("outbox.dividers.sentOn", { date: format(sentDate, "MMMM d, yyyy") }),
			...SENT_STYLE,
		};
	}

	return { text: getScheduledDividerText(category, t), ...SCHEDULED_STYLE };
};
